// Hook: convert a list of {amount, currency} entries to a single home-currency total.
// Fetches all rates in one call (base = homeCcy) and caches per home-currency in memory.
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const _cache = {}; // { homeCcy: { rates: {...}, ts: epoch_ms } }
const TTL = 60 * 60 * 1000; // 1 hour

export function useCurrencyTotal(entries, homeCcy) {
  const home = (homeCcy || "AED").toUpperCase();
  const [rates, setRates] = useState(() => _cache[home]?.rates || null);

  useEffect(() => {
    if (!home) return;
    const c = _cache[home];
    if (c && Date.now() - c.ts < TTL) { setRates(c.rates); return; }
    api.get(`/fx?base=${home}`).then((r) => {
      const rr = r.data?.rates || {};
      _cache[home] = { rates: rr, ts: Date.now() };
      setRates(rr);
    }).catch(() => setRates({}));
  }, [home]);

  const total = useMemo(() => {
    if (!Array.isArray(entries)) return 0;
    return entries.reduce((s, e) => {
      const amt = Number(e?.amount || 0);
      const ccy = (e?.currency || home).toUpperCase();
      if (!amt) return s;
      if (ccy === home) return s + amt;
      // rates[ccy] = how many `ccy` per 1 `home`. So `home = amt / rates[ccy]`.
      const r = rates?.[ccy];
      if (!r || r <= 0) return s; // unknown rate → skip (avoid mis-summing)
      return s + amt / r;
    }, 0);
  }, [entries, rates, home]);

  return { total, ready: rates !== null, rates: rates || {} };
}

// Helper: convert a single amount from `srcCcy` to `homeCcy` using cached rates.
// Returns null if rates not loaded yet or unknown currency.
export function convertAmount(amount, srcCcy, homeCcy, rates) {
  const src = (srcCcy || homeCcy || "AED").toUpperCase();
  const home = (homeCcy || "AED").toUpperCase();
  if (src === home) return Number(amount || 0);
  const r = rates?.[src];
  if (!r || r <= 0) return null;
  return Number(amount || 0) / r;
}
